import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { PageHeader, Panel } from "@/components/primitives";
import { StatusPill } from "@/components/status";
import { useNow, useYard } from "@/lib/use-yard";
import { ON_SITE, fmtClock, fmtDuration, sequenceScore, waitMinutes } from "@/lib/yard-data";

export const Route = createFileRoute("/sequence")({
  head: () => ({
    meta: [
      { title: "Sequence Queue — PortDwellr" },
      { name: "description", content: "See exactly why the queue is ordered the way it is: arrival time, appointment window and trailer type weighting." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AppShell>
      <SequencePage />
    </AppShell>
  ),
});

function Bar({ value, max, label }: { value: number; max: number; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-[64px] font-mono text-[10px] uppercase tracking-[0.1em] text-steel-mid">{label}</span>
      <span className="h-[8px] flex-1 border border-line bg-paper">
        <span className="block h-full bg-steel" style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
      </span>
      <span className="w-[28px] text-right font-mono text-[11px] text-ink-soft">{value}</span>
    </div>
  );
}

function SequencePage() {
  const { trucks } = useYard();
  const now = useNow(2000);

  const queue = trucks
    .filter((t) => ON_SITE.includes(t.status) && t.status !== "in_process")
    .map((t) => ({ t, s: sequenceScore(t, now) }))
    .sort((a, b) => b.s.total - a.s.total);

  return (
    <>
      <PageHeader
        eyebrow="SEQUENCE · queue ordering"
        title="Sequence Queue"
        lead="The order below is not first-come-first-served. Each truck carries a weighting from three inputs — how long it has been waiting, how close its appointment window is, and what it's pulling. The math is shown so a dispatcher can override it with a reason."
      />

      <Panel title="Current order · recalculated continuously">
        <ol>
          {queue.map(({ t, s }, i) => (
            <li key={t.id} className="grid grid-cols-1 gap-5 border-b border-line px-5 py-5 last:border-b-0 md:grid-cols-[64px_1.3fr_1fr] md:px-6">
              <div className="font-mono text-[26px] font-semibold leading-none text-signal-deep">
                {(i + 1).toString().padStart(2, "0")}
              </div>
              <div>
                <div className="font-display text-[17px] font-semibold">
                  Truck {t.queueNo} · {t.carrier}
                </div>
                <div className="mt-1 font-mono text-[12px] text-ink-soft">
                  {t.container} · {t.trailerType} · checked in {fmtClock(t.arrivedAt)} via {t.checkInMethod}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-4">
                  <StatusPill status={t.status} dock={t.dock} />
                  <span className="font-mono text-[12px] text-ink-soft">waiting {fmtDuration(waitMinutes(t, now))}</span>
                  <span className={`font-mono text-[12px] ${s.apptMinutes < 0 ? "text-signal-deep" : "text-ink-soft"}`}>
                    appt {s.apptMinutes < 0 ? `${Math.abs(s.apptMinutes)}m past` : `in ${s.apptMinutes}m`}
                  </span>
                </div>
              </div>
              <div className="space-y-2 border-t border-line pt-4 md:border-l md:border-t-0 md:pl-6 md:pt-0">
                <Bar label="Arrival" value={s.arrivalWeight} max={60} />
                <Bar label="Window" value={s.apptWeight} max={60} />
                <Bar label="Trailer" value={s.trailerWeight} max={60} />
                <div className="pt-1 font-mono text-[11px] uppercase tracking-[0.08em] text-steel">
                  Priority score {s.total}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </Panel>

      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.08em] text-steel-mid">
        Reefers and tankers carry a standing weight bump — power and hazmat windows cost more when missed.
      </p>
    </>
  );
}
